import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { User, Sparkles, LogOut, Zap } from 'lucide-react';
import BrandLogo from './BrandLogo';
import AdSenseUnit from './ads/AdSenseUnit';
import PWAInstallPrompt from './PWAInstallPrompt';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../utils/i18n';
import PremiumModal from './PremiumModal';
import Footer from './Footer';
import { IS_NATIVE } from '../utils/capacitor';

const Layout = ({ user, isPro, signInWithGoogle, logoutUser }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const isProfilePage = location.pathname === '/profile';
  const isPlaylistPage = location.pathname === '/playlist';
  const isPlayerPage = location.pathname === '/player';
  const showSideRailAds = !IS_NATIVE && !isPro && (isProfilePage || isPlaylistPage);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col relative font-sans">
      
      {/* ── TRANSLUCENT GLASS NAVBAR ── */}
      <nav className="sticky top-0 z-[100] bg-slate-950/85 border-b border-white/10 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3.5 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <BrandLogo />
          </Link>

          {!isProfilePage && (
            <div className="hidden md:flex items-center gap-6 text-xs font-extrabold uppercase tracking-wider text-slate-300">
              <a href="/#features" className="hover:text-sky-400 transition">{t('navFeatures')}</a>
              <a href="/#how-it-works" className="hover:text-sky-400 transition">{t('navHowItWorks')}</a>
              <Link
                to="/tools"
                className={`px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 font-extrabold text-amber-400 bg-amber-400/10 border border-amber-400/30 hover:bg-amber-400/20 hover:text-amber-300 shadow-sm shadow-amber-400/10 ${
                  location.pathname === '/tools' ? 'ring-2 ring-amber-400/60 bg-amber-400/25 text-amber-300' : ''
                }`}
              >
                <Zap size={13} className="text-amber-400 fill-amber-400" />
                <span>Tools</span>
              </Link>
              <Link to="/faq" className="hover:text-sky-400 transition">{t('navFaq')}</Link>
              <Link to="/guides" className="hover:text-sky-400 transition">{t('navGuides')}</Link>
            </div>
          )}

          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {/* Language Selector */}
            <LanguageSelector />

            {user ? (
              <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                {!isPro && (
                  <button
                    onClick={() => setShowPremiumModal(true)}
                    className="px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-[11px] sm:text-xs shadow-lg shadow-blue-500/25 flex items-center gap-1 sm:gap-1.5 transition transform active:scale-95 flex-shrink-0"
                    type="button"
                  >
                    <span className="hidden sm:inline">Go Premium</span>
                    <span className="sm:hidden">Pro</span>
                    <Sparkles size={13} />
                  </button>
                )}

                <Link
                  to="/profile"
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition flex-shrink-0"
                >
                  {user.photoURL ? (
                    <img src={user.photoURL} alt="Profile" className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover border border-sky-400 flex-shrink-0" />
                  ) : (
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-slate-300 flex-shrink-0">
                      <User size={13} />
                    </div>
                  )}
                  <span className="text-[11px] sm:text-xs font-bold text-white truncate max-w-[50px] sm:max-w-[100px]">{user.displayName?.split(' ')[0]}</span>
                  {isPro && (
                    <span className="bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-black text-[9px] px-1.5 py-0.5 rounded-full uppercase shadow flex-shrink-0">
                      PRO
                    </span>
                  )}
                </Link>

                <button
                  onClick={logoutUser}
                  className="p-1.5 sm:p-2 rounded-xl bg-white/5 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 border border-white/5 transition flex-shrink-0"
                  title="Sign Out"
                  type="button"
                >
                  <LogOut size={15} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                <button
                  onClick={signInWithGoogle}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] sm:text-xs font-bold text-white transition flex-shrink-0"
                  type="button"
                >
                  <User size={13} />
                  <span>Sign In</span>
                </button>

                <button
                  onClick={() => setShowPremiumModal(true)}
                  className="px-4 py-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-xs shadow-lg shadow-blue-500/25 flex items-center gap-1.5 transition transform active:scale-95"
                  type="button"
                >
                  <span>Go Premium</span>
                  <Sparkles size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <PWAInstallPrompt />
      
      {/* ── MAIN BODY CONTENT ── */}
      <div className="flex-1 flex w-full relative">
        {showSideRailAds && (
          <aside className="w-40 flex-shrink-0 p-2 hidden xl:block">
            <AdSenseUnit
              isPro={isPro}
              adSlot="5086423951"
              lazy={false}
              adFormat={null}
              fullWidthResponsive={false}
              showLabel={false}
              minHeight={600}
              style={{ display: 'inline-block', width: '160px', height: '600px' }}
              containerStyle={{ margin: 0 }}
            />
          </aside>
        )}

        <div className="flex-1 w-full min-w-0">
          <Outlet context={{ user, isPro, openPremiumModal: () => setShowPremiumModal(true) }} />
        </div>
      </div>

      {/* ── GLOBAL FOOTER ── */}
      {!isPlayerPage && <Footer />}

      {showPremiumModal && (
        <PremiumModal
          onClose={() => setShowPremiumModal(false)}
          user={user}
        />
      )}
    </div>
  );
};

export default Layout;
